import { useState } from "react";
import { toast } from "sonner";
import { Check } from "@phosphor-icons/react";
import { useApi, errText } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { openExternal } from "@/lib/redirect";
import { useMe } from "@/components/app/TrialBanner";
import { UsageChart } from "@/components/billing/UsageChart";

const PLANS = [
  { key: "starter_monthly", id: "starter", name: "Starter", price: 29, perks: ["Multi-Sync dashboard", "Zero Cash Date tracking", "25 AI calibrations / day", "Expense categorization"] },
  { key: "pro_monthly", id: "pro", name: "Pro", price: 79, perks: ["Everything in Starter", "100 AI calibrations / day", "Vendor cut-off modeling", "Multi-bank accounts", "Priority support"] },
];

export default function Billing() {
  const api = useApi();
  const { data: me } = useMe();
  const [busy, setBusy] = useState(null);

  const qc = useQueryClient();
  const checkout = async (key) => {
    setBusy(key);
    try {
      if (["starter", "pro"].includes(me?.plan)) {
        const r = await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin });
        if (r.switched) { toast.success(`Switched to ${r.plan === "pro" ? "Pro" : "Starter"}`); await qc.invalidateQueries(); }
        else await openExternal(async () => r.checkout_url);
      } else {
        await openExternal(async () => (await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin })).checkout_url);
      }
    } catch (e) { toast.error(errText(e, "Couldn't start checkout")); }
    setBusy(null);
  };

  return (
    <div data-testid="billing-page">
      <p className="font-mono text-xs text-[#A3A3A3]">BILLING</p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">Your plan</h1>
      <p data-testid="current-plan" className="mt-3 text-[#A3A3A3]">
        Current: <span className="font-mono text-[#00FF9D] uppercase">{me?.plan || "…"}</span>
        {me?.plan === "trial" && <> · trial ends {new Date(me.trial_ends_at).toLocaleDateString()}</>}
      </p>
      <div className="mt-8 grid md:grid-cols-2 gap-4 max-w-4xl">
        {PLANS.map((p) => {
          const current = me?.plan === p.id;
          return (
            <div key={p.key} data-testid={`billing-plan-${p.id}`} className={`rounded-xl border p-7 ${current ? "border-[#00FF9D]" : "border-[#2A2A2A]"} bg-[#121212]`}>
              <h3 className="font-display text-2xl">{p.name}</h3>
              <p className="font-mono mt-4"><span className="text-4xl font-bold">${p.price}</span><span className="text-[#A3A3A3]">/mo</span></p>
              <ul className="mt-6 space-y-2 text-sm">{p.perks.map((k) => <li key={k} className="flex gap-2"><Check className="text-[#00FF9D] mt-0.5" />{k}</li>)}</ul>
              <button data-testid={`checkout-${p.id}-btn`} disabled={current || busy} onClick={() => checkout(p.key)}
                className="mt-8 w-full rounded-full bg-[#00FF9D] py-3 font-semibold text-[#050505] hover:shadow-[0_0_24px_rgba(0,255,157,0.5)] transition-shadow disabled:opacity-40">
                {current ? "Current plan" : busy === p.key ? "Redirecting…" : me?.access === "locked" ? `Subscribe to ${p.name}` : me?.plan && me.plan !== "trial" ? `Switch to ${p.name}` : `Subscribe to ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>
      <p data-testid="billing-trial-note" className="mt-6 text-xs text-[#A3A3A3]">{me?.plan === "trial" && me?.access !== "locked" ? "Your free trial doesn't need a card. Subscribe any time; your remaining trial days carry over and you're charged when they run out." : "Secure checkout by Stripe."}</p>
      {me && me.access !== "locked" && <UsageChart />}
    </div>
  );
}
